import React, { useEffect, useState } from "react";
import Head from "next/head";
import { FaFileUpload } from "react-icons/fa";
import {
  getDocument,
  GlobalWorkerOptions,
} from "pdfjs-dist/legacy/build/pdf.js";
import { AiOutlineCheck } from "react-icons/ai";
import { FaDownload } from "react-icons/fa";
import JSZip from "jszip";
import styles from "../../signature.module.css";
import Blog from "../../../../components/CalculatorBlogs/Blog";
import SignaturePopup from "../../../../components/SignaturePopup";
import PdfToolsLinks from "../../../../components/PdfToolsLinks";

import UploadFile from "@components/PdfTools/UploadFile";
import Steps from "@components/PdfTools/Steps";
import WhyChooseBolosign from "@components/PdfTools/WhyChooseBolosign";
import PricingCards from "@components/PdfTools/PricingCards";
import TryBolosign from "@components/PdfTools/TryBolosign";
import OtherPdfTools from "@components/PdfTools/OtherPdfTools";
import FaqCard from "@components/Cards/FaqCard";
import { LuUpload } from "react-icons/lu";
import { LuDownload } from "react-icons/lu";
import { FaCompressArrowsAlt } from "react-icons/fa";
import { MdPublishedWithChanges } from "react-icons/md";

GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js`;

const steps = [
  {
    icon: <LuUpload />,
    title: "Upload Your PDF",
    description:
      "To change a PDF to a JPG, start by uploading your files. You can drag and drop your PDFs into the conversion area or use the 'Select Files' button to choose which documents to upload.",
  },
  {
    icon: <MdPublishedWithChanges />,
    title: "Convert Your PDFs",
    description:
      "Our tool will begin the conversion process now you can export PDF as JPEG without compromising on quality.",
  },
  {
    icon: <LuDownload />,
    title: "Download Your PDF",
    description:
      "Download your JPG files directly to your device. Our platform ensures that every PDF to JPG conversion is handled with precision, ensuring high-quality results.",
  },
];

const reasons = [
  {
    image: "orientation_autosaving.png", // Replace with the actual image URL
    title: "Orientation autosaving",
    description:
      "Our tool will autosave the orientation of the pages in your file to ensure you don't have to rotate your files when you export a PDF as a JPG.",
  },
  {
    image: "batch.png", // Replace with the actual image URL
    title: "Batch Conversion",
    description: "Convert multiple PDF files to JPG format simultaneously.",
  },
  {
    image: "high_quality.png", // Replace with the actual image URL
    title: "High Quality",
    description:
      "We ensure that your JPGs maintain high resolution and quality.",
  },
  {
    image: "portable_resolution.png", // Replace with the actual image URL
    title: "Portable solution",
    description:
      "Our solution allows you to save a PDF as a PNG on any browser and device. No need to download any software or create an account.",
  },
];

const faqData = [
  {
    faqQuestion: "How do I convert a PDF to a JPEG using BoloSign?",
    faqAnswer:
      "To convert a PDF to a JPEG, simply upload your file to our BoloSign converter. Drag and drop your PDF into the designated area or click 'Select Files' to choose your PDF. Once uploaded, the file will automatically be converted to JPEG format which you can download immediately.",
  },
  {
    faqQuestion:
      "What should I do to change a PDF to a JPG without losing quality?",
    faqAnswer:
      "To ensure high-quality outputs when converting your PDF to JPG, use BoloSign's PDF to JPG converter which maintains the resolution and quality of your original document. Just upload your PDF, and let our tool handle the optimal conversion settings automatically.",
  },
  {
    faqQuestion: "How to save a PDF as JPEG for free?",
    faqAnswer:
      "BoloSign allows you to save your PDF as a JPEG for free without any hidden charges. Access our PDF to JPG tool, upload your PDF, and convert it to JPEG in a few clicks. There is no need to register or provide an email address, making it both simple and secure.",
  },
];

const PdfToJpeg = () => {
  const [pdf, setPdf] = useState(null);
  const [images, setImages] = useState([]);
  const [pageRendering, setPageRendering] = useState(false);
  const [loader, setLoader] = useState(false);
  const [pagesToDownload, setPagesToDownload] = useState([]);
  const [signaturePopup, setSignaturePopup] = useState(false);
  const [error, setError] = useState("");

  const scrollToPreview = () => {
    const element = document.querySelector(".preview-pdfs");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    showPdf(file);
    scrollToPreview();
  };

  const handlePdfUpload = (e) => {
    const file = e.target.files[0];
    showPdf(file);
    if (file) {
      scrollToPreview();
    }
  };

  const handleFileUpload = (files) => {
    if (files[0]) {
      scrollToPreview();
    }
    showPdf(files[0]);
    setError("");
  };

  const showPdf = async (file) => {
    try {
      const uri = URL.createObjectURL(file);
      const _PDF_DOC = await getDocument({ url: uri }).promise;
      setPagesToDownload(
        Array.from({ length: _PDF_DOC.numPages }, (_, i) => i + 1),
      );
      setPdf(_PDF_DOC);
    } catch (error) {
      alert(error.message);
    }
  };

  const renderPage = async () => {
    setPageRendering(true);
    const imagesList = [];

    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const viewport = page.getViewport({ scale: 1 });
      const canvas = document.createElement("canvas");
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      const renderContext = canvas.getContext("2d");
      await page.render({ canvasContext: renderContext, viewport: viewport })
        .promise;
      const img = canvas.toDataURL("image/jpeg");
      imagesList.push(img);
    }

    setImages(imagesList);
    setPageRendering(false);
  };

  const downloadImages = () => {
    if (!images.length > 0) return;
    setLoader(true);
    const fileName = "pdf-to-jpeg.zip";
    const zip = new JSZip();
    const imgFolder = zip.folder("images");
    for (let i = 0; i < images.length; i++) {
      if (!pagesToDownload.includes(i + 1)) continue;
      const base64 = images[i].replace(/^data:image\/(jpeg|jpg);base64,/, "");
      imgFolder.file(`image-${i + 1}.jpeg`, base64, { base64: true });
    }
    zip.generateAsync({ type: "blob" }).then((content) => {
      const url = window.URL.createObjectURL(content);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    });
    setLoader(false);
    setTimeout(() => {
      setSignaturePopup(true);
    }, 2000);
  };

  useEffect(() => {
    pdf && renderPage();
  }, [pdf]);

  const addOrRemovePage = (page) => {
    if (pagesToDownload.includes(page)) {
      setPagesToDownload(pagesToDownload.filter((p) => p !== page));
    } else {
      setPagesToDownload([...pagesToDownload, page]);
    }
  };

  return (
    <div className="w-full mt-20 sm:mt-24">
      <Head>
        <meta charSet="utf-8" />
        <title>PDF to JPEG Tool | BoloForms</title>
        <meta
          name="description"
          content="Convert PDF files to JPEG images online for free. No software installation required."
        />
        <meta
          name="keywords"
          content="pdf to image, pdf to images, pdf to photo, pdf to image boloforms, pdf to JPEG boloforms, pdf to JPEG, pdf to JPEG converter, convert pdf to JPEG, pdf to JPEG online, pdf to JPEG free, pdf to JPEG tool, pdf to JPEG conversion, pdf to JPEG converter online, pdf to JPEG converter free, pdf to JPEG converter tool"
        />
        <meta name="author" content="BoloForms" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <link
          rel="canonical"
          href="https://www.bolosign.com/signature/pdf-tools/pdf-to-jpeg"
        />
        <meta property="og:title" content="PDF to JPEG Tool | BoloForms" />
        <meta
          property="og:description"
          content="Convert PDF files to JPEG images online for free. No software installation required."
        />
        <meta
          property="og:url"
          content="https://www.bolosign.com/signature/pdf-tools/pdf-to-jpeg"
        />
        <meta property="og:site_name" content="BoloForms" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="BoloForms" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_US" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@BoloForms" />
        <meta name="twitter:creator" content="@BoloForms" />
        <meta name="twitter:title" content="PDF to JPEG Tool | BoloForms" />
        <meta
          name="twitter:description"
          content="Convert PDF files to JPEG images online for free. No software installation required."
        />
        <link
          rel="canonical"
          href="https://www.bolosign.com/signature/pdf-tools/pdf-to-jpeg/"
        />
      </Head>
      <div className="w-full">
        <UploadFile
          heading={"Convert Your PDF to JPG Effortlessly with BoloSign"}
          description={`Easily transform your PDF files into high-quality JPG images with our free online converter. Whether you need to convert a single PDF or batch process multiple documents, BoloSign makes it simple and secure.`}
          bgColor={"bg-[#e5dbfd]"}
          btnColor={"bg-[#ab8cfc]"}
          image={"pdf-to-jpeg.png"}
          handleFileUpload={handleFileUpload}
          multipleFiles={false}
        />
        <span className="preview-pdfs"></span>
        <div
          // id="preview-pdfs"
          className="max-w-[1280px] mx-auto flex flex-col gap-14 justify-center items-center mt-12 sm:mt-20 px-4 sm:px-8"
        >
          {pdf && (
            <>
              <h1 className="text-2xl sm:text-3xl font-light text-center text-gray-900">
                Disselect Pages that you {`don't`} want
              </h1>

              <div className="relative w-full h-[400px] flex flex-wrap justify-center gap-4 border-2 md:px-10 py-4 md:py-14 md:rounded-xl overflow-y-scroll scrollbar-thin scrollbar-thumb-violet-500 scrollbar-rounded-[12px]">
                {images.map((image, index) => (
                  <div
                    key={index}
                    className="border w-fit relative flex justify-center items-center flex-wrap"
                  >
                    <div className="border-1 p-4 bg-white w-[230px]">
                      <img
                        id="image-generated"
                        src={image}
                        alt="pdfImage"
                        className="w-[full"
                      />
                    </div>
                    <div className="absolute top-2 left-2 w-8 h-8 bg-black rounded-full text-xl text-white flex justify-center items-center cursor-pointer">
                      {index + 1}
                    </div>
                    <div
                      onClick={() => addOrRemovePage(index + 1)}
                      className={`absolute top-2 right-2 w-8 h-8 ${
                        pagesToDownload.includes(index + 1)
                          ? "bg-violet-500"
                          : "bg-gray-400"
                      } rounded-full text-xl text-white flex justify-center items-center cursor-pointer`}
                    >
                      <AiOutlineCheck />
                    </div>
                  </div>
                ))}
                <div
                  id="page-loader"
                  className={`${
                    pageRendering
                      ? "absolute w-full top-0 left-0 h-full flex justify-center items-center bg-white z-50"
                      : "hidden"
                  }`}
                >
                  <svg
                    aria-hidden="true"
                    className="w-14 h-14 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-violet-600"
                    viewBox="0 0 100 101"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                      fill="currentColor"
                    />
                    <path
                      d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                      fill="currentFill"
                    />
                  </svg>
                </div>
              </div>
            </>
          )}
          {images.length > 0 && (
            <div id="split-pdfs" className="w-full">
              <button
                onClick={downloadImages}
                className="bg-[#e5dbfd] mx-auto w-full sm:w-[350px] py-4 text-xl rounded-lg font-semibold text-gray-900 flex items-center justify-center gap-4 hover:opacity-90 border-2 border-gray-900"
              >
                {loader ? (
                  <div
                    className={`flex items-center text-gray-900 space-x-2 ${styles.loader}`}
                  >
                    <div className="">Downloading</div>
                    <div className="w-2 h-2 bg-gray-900 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-900 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-900 rounded-full animate-bounce"></div>
                  </div>
                ) : (
                  <>
                    Download Zip <FaDownload className="text-3xl" />
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        <div className="w-full px-4 sm:px-8 mt-12">
          <Steps heading={"How to Convert PNG to PDF?"} steps={steps} />
        </div>

        <div className="w-full max-w-screen-xl mx-auto px-4 sm:px-8 mt-16 sm:mt-20">
          <WhyChooseBolosign
            heading={"BoloSign PDF to JPG Converter Features"}
            description={`Our platform is designed with simplicity in mind, making it easy for anyone to convert PND to JPG without any technical expertise. Also, we prioritize your privacy and data protection by processing and storing your documents securely.`}
            reasons={reasons}
          />
        </div>

        <div className="w-full max-w-screen-xl mx-auto px-4 sm:px-8 mt-16 sm:mt-32">
          <PricingCards
            heading={"Looking for more than PDF tools?"}
            description={`Join 50,000+ teams who are already signing and accelerating their document workflow with BoloSign.`}
          />
        </div>

        <div className="w-full max-w-screen-xl mx-auto px-4 sm:px-8 mt-16 sm:mt-32">
          <TryBolosign
            heading={"Try BoloSign Free for 7 Days!"}
            bgColor={"bg-[#e5dbfd]"}
          />
        </div>

        <div className="w-full px-4 sm:px-8 mt-16 sm:mt-32">
          <OtherPdfTools
            heading={`Other PDF Editing Tools`}
            description={`Free online PDF tools to merge, split, rotate, compress, convert, and many more.
Manage your daily PDF tasks with our online PDF tools.`}
          />
        </div>

        {signaturePopup && (
          <SignaturePopup
            onPopupClose={() => setSignaturePopup(false)}
            source="PDF_TO_JPEG"
          />
        )}

        <div className="w-full max-w-screen-xl mx-auto px-4 sm:px-8 mt-16 sm:mt-32 flex flex-col gap-12">
          <h2 className="text-center font-bold leading-[45px] sm:leading-[55px] lg:leading-[70px] text-[36px] sm:text-[48px] lg:text-[60px]">
            Frequently Asked Questions
          </h2>
          <div className="max-w-screen-lg mx-auto w-full flex flex-col gap-6">
            {faqData.map((faq, index) => (
              <FaqCard
                key={index}
                faqQuestion={faq.faqQuestion}
                faqAnswer={faq.faqAnswer}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PdfToJpeg;
